import type * as THREE from 'three';
import { CONFIG } from '../config';
import { PYRAMID_BEERS } from '../content/interior';
import { isBedtime } from '../state/rules';
import type { Interactions } from '../systems/Interactions';
import { HOME_LOTS, homeLot, homeSpots, type HomeFrame } from '../world/home';
import type { Rect, Vec2 } from '../world/layout';
import { Room, windowPaneMaterial, type HomeRoomKind } from '../world/Room';
import { roomSlotAt, type RoomLayout } from '../world/rooms';
import type { Activities } from './Activities';
import type { RestCycle } from './RestCycle';
import type { GameServices } from './services';
import type { IconToken } from '../icons/index';

/** Somebody else's home in your park, as far as their front door and living room go. */
export interface VisitableHome {
  index: number;
  name: string;
  kind: HomeRoomKind;
  walls: string;
  floor: string;
  items: readonly string[];
}

export interface InteriorHooks {
  /** Your lot (index into HOME_LOTS) and what's on it. */
  myIndex(): number;
  myKind(): HomeRoomKind;
  /** Other players' homes in a multiplayer park. */
  neighborHomes(): readonly VisitableHome[];
  openDecorate(): void;
  darkness(): number;
}

interface Visit {
  room: Room;
  home: VisitableHome;
  sig: string;
}

const sigOf = (h: VisitableHome): string => [h.kind, h.walls, h.floor, ...h.items].join('|');

/** A room anyone can walk into: the bar, the shops, the neighbors who'll have you over. */
export interface WalkInRoom {
  readonly slot: number;
  readonly group: THREE.Group;
  readonly bounds: Rect;
  readonly entry: Vec2;
  readonly exit: Vec2;
  updateCutaway(camera: THREE.Vector3): void;
  update(time: number, dt: number): void;
}

/**
 * Going inside: your own trailer (or double wide), and the homes of the other players in
 * your park. Rooms are real places you walk around in. While you're in one the sky goes
 * dark, a warm light comes on overhead, and the walls nearest the camera cut away.
 */
export class Interiors {
  private mine: Room | null = null;
  private readonly visits = new Map<number, Visit>();
  /** The room you're standing in, if any. */
  current: Room | WalkInRoom | null = null;
  /** Rooms anyone can walk into (the bar, the shops), and where their front door is outside. */
  private readonly publicRooms = new Map<number, { room: WalkInRoom; outside: Vec2 }>();
  private outdoorCamera: { pitch: number; distance: number } | null = null;
  private syncTimer = 0;
  private tvUntil = 0;

  constructor(
    private readonly s: GameServices,
    private readonly act: Activities,
    private readonly rest: RestCycle,
    private readonly hooks: InteriorHooks,
  ) {
    rest.wakeInside = () => this.wakeInBed();
    s.player.boundsAt = (x) => {
      const slot = roomSlotAt(x);
      return (slot >= 0 ? this.roomAt(slot)?.bounds : null) ?? CONFIG.bounds;
    };
    s.store.bus.on('interior', () => this.syncMine());
  }

  get inside(): boolean {
    return this.current !== null;
  }

  /** Your own room, for whoever needs it (the decorate menu checks what fits). */
  get myRoom(): Room | null {
    return this.mine;
  }

  private roomAt(slot: number): Room | WalkInRoom | null {
    if (this.mine?.slot === slot) return this.mine;
    return this.visits.get(slot)?.room ?? this.publicRooms.get(slot)?.room ?? null;
  }

  /** A home room in a slot (never the bar), for the per-home interactions. */
  private homeAt(slot: number): Room | null {
    const r = this.roomAt(slot);
    return r instanceof Room ? r : null;
  }

  /** Registers a public room and where you come out when you leave it. */
  addPublic(room: WalkInRoom, outside: Vec2): void {
    this.publicRooms.set(room.slot, { room, outside });
  }

  /** Walk in the front door of a public room. */
  enterPublic(slot: number): void {
    const { player, store } = this.s;
    const p = this.publicRooms.get(slot);
    if (!p || !this.act.canAct()) return;
    if (player.carrying || player.pushing) {
      store.toast('Set that down first. It is not coming inside.', 'bad', ':door:');
      return;
    }
    const e = p.room.entry;
    player.teleport(e.x, e.z, Math.PI);
    this.setCurrent(p.room);
    this.s.sfx('door');
  }

  private ownerName(index: number): string | null {
    return this.hooks.neighborHomes().find((h) => h.index === index)?.name ?? null;
  }

  private kindAt(index: number): HomeRoomKind {
    if (index === this.hooks.myIndex()) return this.hooks.myKind();
    return this.hooks.neighborHomes().find((h) => h.index === index)?.kind ?? 'single';
  }

  // --- rooms -----------------------------------------------------------------------

  /** Builds (or rebuilds) your own room to match your lot, home and decorating. */
  syncMine(): void {
    const { store, player, world } = this.s;
    const slot = this.hooks.myIndex();
    const kind = this.hooks.myKind();
    const st = store.state.interior;
    if (!this.mine || this.mine.slot !== slot || this.mine.kind !== kind) {
      const wasInside = this.current !== null && this.current === this.mine;
      this.mine?.dispose();
      this.mine = new Room(world.dynamic, world.collision, slot, kind, st);
      if (wasInside) {
        const e = this.mine.entry;
        player.teleport(e.x, e.z, Math.PI);
        this.setCurrent(this.mine);
      }
    }
    this.mine.setFinish(st.walls, st.floor);
    this.mine.setDecor(store.displayedInterior());
  }

  /** Builds rooms for the other players' homes, and tears down the ones that left. */
  private syncNeighbors(): void {
    const homes = this.hooks.neighborHomes();
    const mySlot = this.hooks.myIndex();
    for (const h of homes) {
      if (h.index === mySlot) continue;
      const v = this.visits.get(h.index);
      const sig = sigOf(h);
      if (v && v.room.kind === h.kind) {
        if (v.sig !== sig) {
          v.room.setFinish(h.walls, h.floor);
          v.room.setDecor(h.items);
          v.sig = sig;
        }
        v.home = h;
        continue;
      }
      v?.room.dispose();
      const room = new Room(this.s.world.dynamic, this.s.world.collision, h.index, h.kind, h);
      room.setDecor(h.items);
      this.visits.set(h.index, { room, home: h, sig });
    }
    for (const [slot, v] of this.visits) {
      if (homes.some((h) => h.index === slot) && slot !== mySlot) continue;
      // They left: if you were visiting, you find yourself on their (now empty) front step.
      if (this.current === v.room) this.leave(homeLot(slot));
      v.room.dispose();
      this.visits.delete(slot);
    }
  }

  // --- going in and out ----------------------------------------------------------------

  private enterLot(index: number): void {
    const { player, store } = this.s;
    if (!this.act.canAct()) return;
    if (player.carrying || player.pushing) {
      store.toast('Set that down first. It is not coming inside.', 'bad', ':door:');
      return;
    }
    const room = index === this.hooks.myIndex() ? this.mine : (this.visits.get(index)?.room ?? null);
    if (!room) return;
    const e = room.entry;
    player.teleport(e.x, e.z, Math.PI);
    this.setCurrent(room);
    this.s.sfx('door');
    if (room !== this.mine) {
      const name = this.ownerName(index) ?? 'somebody';
      store.toast(`The door's unlocked. It's always unlocked. Welcome to ${name}'s place.`, 'info', ':door:');
    }
  }

  leave(frame?: HomeFrame): void {
    const room = this.current;
    const pub = room ? this.publicRooms.get(room.slot) : undefined;
    if (pub && !frame) {
      this.s.player.teleport(pub.outside.x, pub.outside.z, 0);
      this.setCurrent(null);
      this.s.sfx('door');
      return;
    }
    const index = frame?.index ?? room?.slot ?? this.hooks.myIndex();
    const door = homeSpots(homeLot(index), this.kindAt(index)).door;
    this.s.player.teleport(door.x, door.z + 0.4, 0);
    this.setCurrent(null);
    this.s.sfx('door');
  }

  /** You're in whatever room your feet are in; this flips the camera and lights to match. */
  private setCurrent(room: Room | WalkInRoom | null): void {
    const { cameraRig, effects } = this.s;
    const was = this.current;
    this.current = room;
    if (room) {
      if (!was) this.outdoorCamera = { pitch: cameraRig.pitch, distance: cameraRig.distance };
      cameraRig.useOccluders = false;
      cameraRig.minPitch = 0.6;
      cameraRig.maxDistance = 11;
      cameraRig.pitch = Math.max(cameraRig.pitch, 0.85);
      cameraRig.distance = 7.5;
      effects.indoors = true;
    } else {
      cameraRig.useOccluders = true;
      cameraRig.minPitch = 0.08;
      cameraRig.maxDistance = 16;
      if (this.outdoorCamera) {
        cameraRig.pitch = this.outdoorCamera.pitch;
        cameraRig.distance = this.outdoorCamera.distance;
      }
      this.outdoorCamera = null;
      effects.indoors = false;
    }
    cameraRig.snap();
  }

  /** After a night's sleep in your own bed you wake up next to it. */
  private wakeInBed(): boolean {
    if (!this.mine || this.current !== this.mine) return false;
    const { player, store } = this.s;
    const b = this.mine.spot('bed');
    player.teleport(b.x, b.z, 0);
    this.s.cameraRig.snap();
    if (this.mine.has('waterbed')) {
      store.apply({ stats: { energy: 15, health: 10 } });
      store.toast('Slept like a baby on the high seas. +15 :bolt: +10 :heart:', 'good', ':bed:');
    }
    return true;
  }

  // --- per frame -------------------------------------------------------------------

  update(dt: number, camera: THREE.Camera): void {
    const p = this.s.player.position;
    const slot = roomSlotAt(p.x);
    const room = slot >= 0 ? this.roomAt(slot) : null;
    if (room !== this.current) this.setCurrent(room);
    // Only the room you're in gets drawn: the rest are hundreds of meters off in the dark.
    this.showOnly(this.current);
    this.syncTimer -= dt;
    if (this.syncTimer <= 0) {
      this.syncTimer = 1;
      this.syncNeighbors();
      this.checkPyramid();
    }
    if (!this.current) return;
    this.current.updateCutaway(camera.position);
    if (this.current instanceof Room) this.current.tvOn = this.s.now() < this.tvUntil;
    this.current.update(this.s.now(), dt);
    // The view out the windows follows the time of day.
    const d = this.hooks.darkness();
    const pane = windowPaneMaterial();
    pane.color.setRGB(0.81 - 0.7 * d, 0.9 - 0.75 * d, 0.96 - 0.7 * d);
    pane.emissive.copy(pane.color);
    pane.emissiveIntensity = 0.85 - 0.6 * d;
  }

  private showOnly(room: Room | WalkInRoom | null): void {
    const all: Array<Room | WalkInRoom> = [
      ...(this.mine ? [this.mine] : []),
      ...[...this.visits.values()].map((v) => v.room),
      ...[...this.publicRooms.values()].map((p) => p.room),
    ];
    for (const r of all) {
      const show = r === room;
      if (r.group.visible !== show) r.group.visible = show;
    }
  }

  /** Fifty beers in, the pyramid builds itself. */
  private checkPyramid(): void {
    const { store } = this.s;
    if (store.counter('beers') < PYRAMID_BEERS || store.hasInterior('canPyramid')) return;
    store.addInterior('canPyramid');
    store.toast(
      'Fifty empties and counting. You built a BEER CAN PYRAMID in the living room.',
      'meme',
      ':empty-can:',
    );
  }

  // --- interactions ----------------------------------------------------------------

  register(interactions: Interactions): void {
    const { store, player, ui } = this.s;
    const add = interactions.add.bind(interactions);
    const busy = (seconds: number, done: () => void): void =>
      player.doBusy({ pose: 'stand', until: this.s.now() + seconds, onDone: done });

    // Front doors, yours and your neighbors'.
    for (const f of HOME_LOTS) {
      add({
        id: `enter-${f.index}`,
        at: () => homeSpots(f, this.kindAt(f.index)).door,
        radius: 1.6,
        prompt: () => {
          if (f.index === this.hooks.myIndex()) return this.mine ? 'Go inside' : null;
          const name = this.ownerName(f.index);
          return name && this.visits.has(f.index) ? `Go inside ${name}'s place` : null;
        },
        action: () => this.enterLot(f.index),
        priority: 0.4,
        icon: ':door:',
      });
    }

    // Inside, per room slot. Each looks up whatever room is in that slot right now.
    const far: Vec2 = { x: 99999, z: 99999 };
    for (const f of HOME_LOTS) {
      const slot = f.index;
      const room = (): Room | null => this.homeAt(slot);
      const mine = (): boolean => !!this.mine && this.mine.slot === slot;
      const local = (name: keyof RoomLayout['spots']) => (): Vec2 => room()?.spot(name) ?? far;
      add({
        id: `room-${slot}-exit`,
        at: () => room()?.exit ?? far,
        radius: 1.2,
        prompt: () => (room() ? 'Head outside' : null),
        action: () => this.leave(),
        priority: 0.5,
        icon: ':door:',
      });
      add({
        id: `room-${slot}-bed`,
        at: local('bed'),
        radius: 1.5,
        icon: ':bed:',
        highlight: () => room()?.objectFor('waterbed') ?? room()?.objectFor('bed') ?? null,
        prompt: () =>
          !room() ? null : !mine() ? null : isBedtime(store.minutes) ? "Sleep till mornin'" : 'Take a nap',
        action: () => {
          if (isBedtime(store.minutes)) this.rest.sleep();
          else if (store.stat('energy') >= 85)
            store.toast("You're not tired. You're just avoiding things.", 'info', ':bed:');
          else this.rest.nap();
        },
      });
      add({
        id: `room-${slot}-couch`,
        at: local('couch'),
        radius: 1.4,
        icon: ':couch:',
        highlight: () => room()?.objectFor('couch') ?? null,
        prompt: () =>
          room() ? (store.has('beer') ? 'Kick back with a cold one & the TV' : 'Kick back & watch TV') : null,
        action: () => this.act.watchTv(room() ?? undefined),
      });
      add({
        id: `room-${slot}-tv`,
        at: local('tv'),
        radius: 1.3,
        icon: ':tv:',
        highlight: () => room()?.objectFor('tv') ?? null,
        prompt: () => (room() ? 'Watch TV' : null),
        action: () => this.act.watchTv(room() ?? undefined),
      });
      add({
        id: `room-${slot}-fridge`,
        at: local('fridge'),
        radius: 1.3,
        icon: ':fried-chicken:',
        highlight: () => room()?.objectFor('fridge') ?? null,
        prompt: () => {
          if (!room()) return null;
          if (mine()) return 'Fix a plate';
          return store.doneToday(`raid_${slot}`) ? null : 'Raid their fridge';
        },
        action: () => {
          if (mine()) return this.act.eat();
          store.markToday(`raid_${slot}`);
          this.s.sfx('crunch');
          store.apply({ stats: { hunger: 20 } });
          store.toast(
            `You ate ${this.ownerName(slot) ?? 'their'}'s leftover casserole straight out of the dish. They'll never know.`,
            'meme',
            ':casserole:',
          );
        },
      });
      add({
        id: `room-${slot}-toilet`,
        at: local('toilet'),
        radius: 1.1,
        icon: ':toilet:',
        highlight: () => room()?.objectFor('toilet') ?? null,
        prompt: () => (room() ? 'Use the facilities' : null),
        action: () =>
          busy(2.5, () => {
            store.apply({ stats: { energy: 3 }, minutes: 10 });
            store.toast(
              this.s.rng.pick([
                'You read Bass Fishing Monthly, March 2006, cover to cover. Again.',
                'The fan in here sounds like a jet engine and moves zero air.',
                "Somebody wrote 'DALE WUZ HERE' on the toilet paper roll. How.",
              ]),
              'info',
              ':toilet:',
            );
          }),
      });
      add({
        id: `room-${slot}-decorate`,
        at: () => {
          const r = room();
          return r && mine() ? r.world({ x: r.shape.doorX - 1.3, z: r.shape.depth / 2 - 0.6 }) : far;
        },
        radius: 1.0,
        prompt: () => (room() && mine() ? 'Decorate (paint, carpet, your stuff)' : null),
        action: () => this.hooks.openDecorate(),
        priority: 0.2,
        icon: ':paint:',
      });

      // Things you've decorated with, and what they do.
      const ICONS: Record<string, IconToken> = {
        singingBass: ':singing-bass:',
        beerFridge: ':beer-fridge:',
        fishTank: ':fish-tank:',
        karaoke: ':microphone:',
        tanningBed: ':tanning-bed:',
        gameConsole: ':gamepad:',
        lazyBoy: ':recliner:',
      };
      const item = (id: string, prompt: () => string | null, action: () => void, radius = 1.5): void =>
        add({
          id: `room-${slot}-${id}`,
          at: () => {
            const r = room();
            return r && mine() && r.has(id) ? (r.decorSpot(id) ?? far) : far;
          },
          radius,
          prompt,
          action,
          icon: ICONS[id] ?? ':sparkles:',
          highlight: () => room()?.objectFor(id) ?? null,
        });
      item(
        'singingBass',
        () => 'Push the button on the singing bass',
        () => {
          room()?.singBass?.();
          this.s.sfx('whistle');
          if (!store.doneToday('bass')) {
            store.markToday('bass');
            store.addClout(1);
          }
          ui.hud.float(':notes: Take me to the river :notes:', player.headPosition(), '#9dff6a');
        },
      );
      item(
        'beerFridge',
        () => (store.doneToday('beerFridge') ? null : 'Grab a cold one from the beer fridge'),
        () => {
          store.markToday('beerFridge');
          store.give('beer', 1);
          this.s.sfx('canOpen');
          store.toast("The beer fridge provides. Nobody knows who restocks it. Don't ask.", 'good', ':ice:');
        },
      );
      item(
        'fishTank',
        () => 'Watch Kevin swim',
        () =>
          busy(3, () => {
            store.apply({ stats: { energy: 6 }, minutes: 15 });
            store.toast('Kevin swims left. Kevin swims right. You feel at peace.', 'good', ':goldfish:');
          }),
      );
      item(
        'karaoke',
        () => (store.doneToday('karaoke') ? null : 'Sing karaoke'),
        () =>
          busy(3, () => {
            store.markToday('karaoke');
            this.s.sfx('crowd');
            store.apply({ clout: 3, minutes: 30 });
            store.toast(
              'You belted "Friends in Low Places" at full volume. Three dogs joined in.',
              'meme',
              ':microphone:',
            );
          }),
      );
      item(
        'tanningBed',
        () => (store.doneToday('tan') ? null : 'Fake bake in the tanning bed'),
        () =>
          busy(3, () => {
            store.markToday('tan');
            store.apply({ clout: 4, stats: { health: -3 }, minutes: 20 });
            store.toast('Bronzed. A little crispy around the edges. Worth it.', 'meme', ':sun:');
          }),
        1.8,
      );
      item(
        'gameConsole',
        () => 'Play some video games',
        () =>
          busy(3, () => {
            store.apply({ stats: { energy: 8 }, minutes: 45 });
            store.toast(
              'You lost to a 12-year-old online. He called you a name you had to look up.',
              'info',
              ':gamepad:',
            );
          }),
      );
      item(
        'lazyBoy',
        () => 'Kick back in the recliner',
        () =>
          busy(3, () => {
            store.apply({ stats: { energy: 25 }, minutes: 60 });
            store.toast(
              'You reclined so hard you saw the face of God. He was also reclining.',
              'good',
              ':couch:',
            );
          }),
      );
    }
  }
}
